import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';

type Lang = 'fr' | 'en' | 'es';

interface EditServerData {
  name: string;
  description: string;
  ip_address: string;
  version?: string;
  website_url?: string;
  discord_url?: string;
  tags: string[];
  logo_image_url?: string | null;
  banner_image_url?: string | null;
  description_image_url?: string | null;
  custom_color?: string;
  game_id?: string;
}

/** Normalise une langue potentielle vers 'fr' | 'en' | 'es' (fallback FR) */
function normalizeLang(input?: string | null): Lang {
  const v = String(input ?? '').toLowerCase();
  if (v.startsWith('en')) return 'en';
  if (v.startsWith('es')) return 'es';
  return 'fr';
}

/** Essaie de déduire la langue depuis l’URL (/en/..., /es/..., /fr/...) */
function getLangFromPath(pathname?: string | null): Lang | undefined {
  if (!pathname) return undefined;
  const seg = pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase();
  if (seg === 'en' || seg === 'es' || seg === 'fr') return seg as Lang;
  return undefined;
}

/** Résout la langue à utiliser pour les toasts (URL > html[lang] > contexte > navigateur) */
function resolveUILang(ctxLanguage?: string | null): Lang {
  const pathLang =
    typeof window !== 'undefined' ? getLangFromPath(window.location.pathname) : undefined;
  if (pathLang) return pathLang;

  const htmlLang =
    typeof document !== 'undefined'
      ? document.documentElement.getAttribute('lang')
      : null;
  if (htmlLang) return normalizeLang(htmlLang);

  if (ctxLanguage) return normalizeLang(ctxLanguage);

  const navLang = typeof navigator !== 'undefined' ? navigator.language : 'fr';
  return normalizeLang(navLang);
}

const MESSAGES: Record<
  Lang,
  {
    edit: { title: string; desc: string; errTitle: string; errDesc: string };
    del: { title: string; desc: string; errTitle: string; errDesc: string };
  }
> = {
  fr: {
    edit: {
      title: 'Serveur modifié',
      desc: 'Les modifications ont été enregistrées avec succès.',
      errTitle: 'Erreur',
      errDesc:
        'Une erreur est survenue lors de la modification du serveur.',
    },
    del: {
      title: 'Serveur supprimé',
      desc: 'Le serveur a été supprimé avec succès.',
      errTitle: 'Erreur',
      errDesc:
        'Une erreur est survenue lors de la suppression du serveur.',
    },
  },
  en: {
    edit: {
      title: 'Server updated',
      desc: 'Your changes have been saved successfully.',
      errTitle: 'Error',
      errDesc:
        'An error occurred while updating the server.',
    },
    del: {
      title: 'Server deleted',
      desc: 'The server was deleted successfully.',
      errTitle: 'Error',
      errDesc:
        'An error occurred while deleting the server.',
    },
  },
  es: {
    edit: {
      title: 'Servidor actualizado',
      desc: 'Los cambios se han guardado correctamente.',
      errTitle: 'Error',
      errDesc:
        'Se produjo un error al actualizar el servidor.',
    },
    del: {
      title: 'Servidor eliminado',
      desc: 'El servidor se eliminó correctamente.',
      errTitle: 'Error',
      errDesc:
        'Se produjo un error al eliminar el servidor.',
    },
  },
};

export const useEditServer = () => {
  const queryClient = useQueryClient();
  const { language: ctxLanguage } = useLanguage();
  const uiLang = resolveUILang(ctxLanguage);
  const T = MESSAGES[uiLang];

  const editMutation = useMutation({
    mutationFn: async ({
      serverId,
      data,
    }: {
      serverId: string;
      data: EditServerData;
    }) => {
      const updateData = {
        ...data,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('servers')
        .update(updateData)
        .eq('id', serverId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalider les caches concernés
      queryClient.invalidateQueries({ queryKey: ['user-servers'] });
      queryClient.invalidateQueries({ queryKey: ['servers'] });

      toast({
        title: T.edit.title,
        description: T.edit.desc,
      });
    },
    onError: (error) => {
      console.error('Edit server error:', error);
      toast({
        title: T.edit.errTitle,
        description: T.edit.errDesc,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const { error } = await supabase
        .from('servers')
        .delete()
        .eq('id', serverId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-servers'] });
      queryClient.invalidateQueries({ queryKey: ['servers'] });

      toast({
        title: T.del.title,
        description: T.del.desc,
      });
    },
    onError: (error) => {
      console.error('Delete server error:', error);
      toast({
        title: T.del.errTitle,
        description: T.del.errDesc,
        variant: 'destructive',
      });
    },
  });

  return {
    editServer: editMutation.mutate,
    deleteServer: deleteMutation.mutate,
    isEditingServer: editMutation.isPending,
    isDeletingServer: deleteMutation.isPending,
  };
};
